<template>
  <b-card no-body class="custom-card shadow-none custom-accordion mb-3">
    <b-card-header header-tag="header" role="tab" class="bg-white shadow-sm border-bottom-0">
      <a v-b-toggle="'accordion-files-' + entityType" class="text-primary d-block font-weight-bold" href="javascript: void(0);">
        <h5 class="m-0"><i class="bx bx-file mr-2"></i>{{ $t("file-manager.files") }} <span v-if="files && files.length">({{ files.length }})</span></h5>
      </a>
    </b-card-header>
    <b-collapse :id="'accordion-files-' + entityType" role="tabpanel" v-model="isOpen">
      <b-card-body>
        <!-- Upload Section -->
        <div v-if="canUpload" class="mb-3">
          <button class="link-button mb-3" @click="showUploader = !showUploader">
            <i class="iconoir-attachment"></i> {{ $t("file-manager.upload-from-disk") }}
          </button>
          <div v-if="showUploader" class="ml-2 mt-2 mb-4">
            <file-rocket
              :multiple="true"
              :allowedFormats="allowedFormats"
              @response="handleUploadResponse"
              :showProgress="'Upload'"
              :maxFileSize="600000000000"
              :maxFileLength="1"
              :targetUrl="targetUrl"
            >
            </file-rocket>
          </div>
        </div>

        <!-- File List -->
        <div v-if="files && files.length > 0">
          <div class="row">
            <div class="col-md-3 d-flex" v-for="file in paginatedFiles" :key="file._id">
              <div class="card shadow-none border" style="width: 100%;">
                <div class="card-body text-center d-flex align-items-center flex-column p-3">
                  <div v-b-tooltip.hover :title="$options.filters.translate(file.name)" class="mb-2">
                    <file-icon class="hand" :extension="file.extension" />
                    <p class="card-text mt-1 mb-0" style="font-size: 0.85rem;">{{ file.name | translate | summary(15) }}</p>
                  </div>
                  <div class="mt-auto">
                    <i @click="downloadFile(file)" v-b-tooltip.hover :title="$t('file-manager.download')" class="bx bxs-download text-dark hand mr-2" style="font-size:1.2rem;"></i>
                    <i v-if="canUpload" @click="deleteFile(file)" v-b-tooltip.hover :title="$t('common.delete')" class="bx bx-trash text-danger hand" style="font-size:1.2rem;"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <b-pagination
            class="mt-2"
            v-if="files.length > filePagination.perPage"
            v-model="filePagination.currentPage"
            :total-rows="files.length"
            :per-page="filePagination.perPage"
            limit="5"
            size="sm"
          ></b-pagination>
        </div>
        <div v-else class="text-center text-muted py-3">
          {{ $t("common.no-data") }}
        </div>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
import FileRocket from "@c/form/file-rocket";
import FileIcon from "@modules/file-manager/file-icon.vue";

export default {
  name: "EntityFileSection",
  components: { FileRocket, FileIcon },
  props: {
    entityType: {
      type: String,
      required: true, // 'control', 'controlTest', 'finding', 'risk'
    },
    entityId: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    canUpload: {
      type: Boolean,
      default: false,
    },
    uploadBaseUrl: {
      type: String,
      required: true, // e.g. 'audit-control/control', 'finding', 'risk'
    },
  },
  data() {
    return {
      showUploader: false,
      isOpen: false,
      allowedFormats: ["application/*", "image/*", "text/*"],
      filePagination: {
        currentPage: 1,
        perPage: 4,
      },
    };
  },
  computed: {
    targetUrl() {
      return `${this.uploadBaseUrl}/${this.entityId}/upload`;
    },
    isUploading() {
      return this.$store.getters["fileManager/isUploading"];
    },
    paginatedFiles() {
      if (!this.files) return [];
      const start = (this.filePagination.currentPage - 1) * this.filePagination.perPage;
      const end = start + this.filePagination.perPage;
      return this.files.slice(start, end);
    },
  },
  methods: {
    handleUploadResponse(event) {
      if (!this.isUploading) {
        if (event && event?.status !== 200) {
          this.$handleHttpErrorByStatusCode(event?.error);
        }
        setTimeout(() => {
          this.$emit("uploaded");
        }, 1000);
      }
    },
    downloadFile(file) {
      this.$store.commit("fileManager/addDownloadedFile", { file });
    },
    async deleteFile(item) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) return;
      try {
        await this.$store.dispatch("fileManager/delete", item._id);
        this.$emit("uploaded");
        this.$toast.success(`${this.$t("common.delete-successful")} [${this.$options.filters.translate(item.name)}]`);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error, { customeMessage: `${this.$t("common.delete-fail")} [${this.$options.filters.translate(item.name)}]` });
      }
    },
  },
};
</script>

<style scoped>
.link-button {
  background: none;
  border: none;
  color: #556ee6;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0;
}
.link-button:hover {
  text-decoration: underline;
}
</style>
